// SMSF UI Integration Components - SMSF-specific input forms and result displays
// Integration with existing calculator interfaces
// Last updated: September 2025

import React, { useState, useEffect } from 'react';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';
import { SMSFFeeEngine } from './SMSFFeeEngine.js';
import { SMSFComplianceEngine } from './SMSFComplianceEngine.js';
import { FinancialConfig } from './FinancialConfig.js';

// SMSF Configuration Input Form
export const SMSFConfigurationForm = ({ onConfigurationChange, initialConfig = {}, inputsLocked = false }) => {
    const [config, setConfig] = useState({
        fundName: initialConfig.fundName || '',
        abn: initialConfig.abn || '',
        tfn: initialConfig.tfn || '',
        establishmentDate: initialConfig.establishmentDate || '',
        trusteeType: initialConfig.trusteeType || 'individual',
        numberOfMembers: initialConfig.numberOfMembers || 2,
        // Treat balance as single source; keep fundBalance as UI label but mirror to balance
        fundBalance: (typeof initialConfig.balance === 'number' ? initialConfig.balance : initialConfig.fundBalance) || 0,
        balance: (typeof initialConfig.balance === 'number' ? initialConfig.balance : initialConfig.fundBalance) || 0,
        hasLRBA: initialConfig.hasLRBA || false,
        hasCollectibles: initialConfig.hasCollectibles || false,
        hasInHouseAssets: initialConfig.hasInHouseAssets || false,
        // Individual Trustee Details (up to 4)
        trustee1Name: initialConfig.trustee1Name || '',
        trustee1TFN: initialConfig.trustee1TFN || '',
        trustee1DOB: initialConfig.trustee1DOB || '',
        trustee2Name: initialConfig.trustee2Name || '',
        trustee2TFN: initialConfig.trustee2TFN || '',
        trustee2DOB: initialConfig.trustee2DOB || '',
        trustee3Name: initialConfig.trustee3Name || '',
        trustee3TFN: initialConfig.trustee3TFN || '',
        trustee3DOB: initialConfig.trustee3DOB || '',
        trustee4Name: initialConfig.trustee4Name || '',
        trustee4TFN: initialConfig.trustee4TFN || '',
        trustee4DOB: initialConfig.trustee4DOB || '',
        // Corporate Trustee Details
        corporateTrusteeName: initialConfig.corporateTrusteeName || '',
        corporateACN: initialConfig.corporateACN || '',
        corporateDirector1: initialConfig.corporateDirector1 || '',
        corporateDirector2: initialConfig.corporateDirector2 || '',
        // Member Details (up to 4 members)
        member1Name: initialConfig.member1Name || '',
        member1TFN: initialConfig.member1TFN || '',
        member1DOB: initialConfig.member1DOB || '',
        member1Address: initialConfig.member1Address || '',
        member1Type: initialConfig.member1Type || 'accumulation',
        member1Balance: initialConfig.member1Balance || 0,
        member1TBCUsed: initialConfig.member1TBCUsed || 0,
        member1ConcessionalUsed: initialConfig.member1ConcessionalUsed || 0,
        member1NonConcessionalUsed: initialConfig.member1NonConcessionalUsed || 0,
        member2Name: initialConfig.member2Name || '',
        member2TFN: initialConfig.member2TFN || '',
        member2DOB: initialConfig.member2DOB || '',
        member2Address: initialConfig.member2Address || '',
        member2Type: initialConfig.member2Type || 'accumulation',
        member2Balance: initialConfig.member2Balance || 0,
        member2TBCUsed: initialConfig.member2TBCUsed || 0,
        member2ConcessionalUsed: initialConfig.member2ConcessionalUsed || 0,
        member2NonConcessionalUsed: initialConfig.member2NonConcessionalUsed || 0,
        member3Name: initialConfig.member3Name || '',
        member3TFN: initialConfig.member3TFN || '',
        member3DOB: initialConfig.member3DOB || '',
        member3Address: initialConfig.member3Address || '',
        member3Type: initialConfig.member3Type || 'accumulation',
        member3Balance: initialConfig.member3Balance || 0,
        member3TBCUsed: initialConfig.member3TBCUsed || 0,
        member3ConcessionalUsed: initialConfig.member3ConcessionalUsed || 0,
        member3NonConcessionalUsed: initialConfig.member3NonConcessionalUsed || 0,
        member4Name: initialConfig.member4Name || '',
        member4TFN: initialConfig.member4TFN || '',
        member4DOB: initialConfig.member4DOB || '',
        member4Address: initialConfig.member4Address || '',
        member4Type: initialConfig.member4Type || 'accumulation',
        member4Balance: initialConfig.member4Balance || 0,
        member4TBCUsed: initialConfig.member4TBCUsed || 0,
        member4ConcessionalUsed: initialConfig.member4ConcessionalUsed || 0,
        member4NonConcessionalUsed: initialConfig.member4NonConcessionalUsed || 0,
        member5Name: initialConfig.member5Name || '',
        member5TFN: initialConfig.member5TFN || '',
        member5DOB: initialConfig.member5DOB || '',
        member5Address: initialConfig.member5Address || '',
        member5Type: initialConfig.member5Type || 'accumulation',
        member5Balance: initialConfig.member5Balance || 0,
        member5TBCUsed: initialConfig.member5TBCUsed || 0,
        member5ConcessionalUsed: initialConfig.member5ConcessionalUsed || 0,
        member5NonConcessionalUsed: initialConfig.member5NonConcessionalUsed || 0,
        member6Name: initialConfig.member6Name || '',
        member6TFN: initialConfig.member6TFN || '',
        member6DOB: initialConfig.member6DOB || '',
        member6Address: initialConfig.member6Address || '',
        member6Type: initialConfig.member6Type || 'accumulation',
        member6Balance: initialConfig.member6Balance || 0,
        member6TBCUsed: initialConfig.member6TBCUsed || 0,
        member6ConcessionalUsed: initialConfig.member6ConcessionalUsed || 0,
        member6NonConcessionalUsed: initialConfig.member6NonConcessionalUsed || 0,
        ...initialConfig
    });

    const [lastSaved, setLastSaved] = useState(Date.now());
    const [saveMessage, setSaveMessage] = useState('');

    // Keep form in sync if initialConfig changes (prevents stale or zeroed values)
    useEffect(() => {
        const next = {
            ...config,
            ...initialConfig
        };
        const nextBalance = (typeof initialConfig.balance === 'number' ? initialConfig.balance : initialConfig.fundBalance) ?? next.fundBalance ?? next.balance ?? 0;
        next.fundBalance = Number(nextBalance) || 0;
        next.balance = Number(nextBalance) || 0;
        setConfig(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialConfig.fundBalance, initialConfig.balance]);

    const handleInputChange = (field, value) => {
        if (inputsLocked) return; // Prevent changes when locked
        const newConfig = { ...config, [field]: value };
        // Mirror fundBalance to balance so validators/dashboards see the change immediately
        if (field === 'fundBalance') {
            const numeric = parseFloat(value) || 0;
            newConfig.balance = numeric;
        }
        setConfig(newConfig);
        // Always propagate both fields so upstream state remains consistent
        const outbound = { 
            ...newConfig, 
            balance: typeof newConfig.balance === 'number' ? newConfig.balance : (parseFloat(newConfig.fundBalance) || 0),
            totalAssets: typeof newConfig.totalAssets === 'number' ? newConfig.totalAssets : (parseFloat(newConfig.fundBalance) || 0)
        };
        onConfigurationChange(outbound);
        setLastSaved(Date.now());
        setSaveMessage('Saving...');
        setTimeout(() => setSaveMessage('Saved'), 500);
    };

    const handleSave = () => {
        const outbound = { 
            ...config, 
            balance: typeof config.balance === 'number' ? config.balance : (parseFloat(config.fundBalance) || 0),
            totalAssets: typeof config.totalAssets === 'number' ? config.totalAssets : (parseFloat(config.fundBalance) || 0)
        };
        onConfigurationChange(outbound);
        setLastSaved(Date.now());
        setSaveMessage('Configuration saved successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
    };

    // Autosave indicator update
    useEffect(() => {
        const interval = setInterval(() => {
            setLastSaved(prev => prev); // Force re-render for time display
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const getSaveTime = () => {
        const date = new Date(lastSaved);
        return date.toLocaleTimeString('en-AU', { 
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit',
            hour12: false 
        });
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">SMSF Configuration</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Basic Fund Information */}
                <div className="space-y-4">
                    <h4 className="font-medium text-gray-700">Basic Information</h4>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Fund Name
                        </label>
                        <input
                            type="text"
                            value={config.fundName}
                            onChange={(e) => handleInputChange('fundName', e.target.value)}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            placeholder="Enter SMSF name"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            ABN
                        </label>
                        <input
                            type="text"
                            value={config.abn}
                            onChange={(e) => handleInputChange('abn', e.target.value)}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                            placeholder="11-digit ABN"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            TFN
                        </label>
                        <input
                            type="text"
                            value={config.tfn}
                            onChange={(e) => handleInputChange('tfn', e.target.value)}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                            placeholder="9-digit TFN"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Establishment Date
                        </label>
                        <input
                            type="date"
                            value={config.establishmentDate}
                            onChange={(e) => handleInputChange('establishmentDate', e.target.value)}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                        />
                    </div>
                </div>

                {/* Fund Structure */}
                <div className="space-y-4">
                    <h4 className="font-medium text-gray-700">Fund Structure</h4>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Trustee Type
                        </label>
                        <select
                            value={config.trusteeType}
                            onChange={(e) => handleInputChange('trusteeType', e.target.value)}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                        >
                            <option value="individual">Individual Trustees</option>
                            <option value="corporate">Corporate Trustee</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Number of Members
                        </label>
                        <input
                            {...getNumericInputProps()}
                            min="1"
                            max="6"
                            value={config.numberOfMembers}
                            onChange={(e) => handleInputChange('numberOfMembers', parseInt(e.target.value))}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Fund Balance ($) <span className="text-xs text-gray-500">(auto-calculated from Valuation tab)</span>
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={config.fundBalance}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                            placeholder="Enter assets in Valuation tab"
                            title="This value is automatically calculated from your assets in the Valuation tab. Go to Investment & Assets → Valuation to manage your fund's assets."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            💡 This is automatically calculated from your assets in the <strong>Valuation tab</strong>. 
                            To update, go to <strong>Investment & Assets → Valuation</strong>.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <h5 className="font-medium text-gray-600">Fund Features</h5>
                        
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                disabled={inputsLocked}
                                checked={config.hasLRBA}
                                onChange={(e) => handleInputChange('hasLRBA', e.target.checked)}
                                className="mr-2"
                            />
                            <span className="text-sm">Limited Recourse Borrowing Arrangement (LRBA)</span>
                        </label>

                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                disabled={inputsLocked}
                                checked={config.hasCollectibles}
                                onChange={(e) => handleInputChange('hasCollectibles', e.target.checked)}
                                className="mr-2"
                            />
                            <span className="text-sm">Collectibles</span>
                        </label>

                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                disabled={inputsLocked}
                                checked={config.hasInHouseAssets}
                                onChange={(e) => handleInputChange('hasInHouseAssets', e.target.checked)}
                                className="mr-2"
                            />
                            <span className="text-sm">In-House Assets</span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Member Details Section */}
            <div className="mt-6 p-6 bg-blue-50 rounded-lg border-2 border-blue-300">
                <h4 className="font-medium text-gray-800 mb-2">👥 SMSF Member Details</h4>
                <p className="text-sm text-gray-600 mb-4">
                    Enter details for each fund member. This information is required for ATO reporting and compliance.
                </p>
                
                <div className="space-y-6">
                    {/* Dynamically render members based on numberOfMembers */}
                    {[1, 2, 3, 4, 5, 6].slice(0, Math.min(config.numberOfMembers, 6)).map((num) => {
                        const memberPrefix = `member${num}`;
                        return (
                            <div key={num} className="p-4 bg-white rounded-lg border border-blue-200">
                                <h5 className="font-semibold text-gray-700 mb-4">Member {num}</h5>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Full Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={config[`${memberPrefix}Name`] || ''}
                                            onChange={(e) => handleInputChange(`${memberPrefix}Name`, e.target.value)}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                            placeholder="e.g. John Smith"
                                        />
                                    </div>

                                    {/* TFN */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Tax File Number (TFN) <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={config[`${memberPrefix}TFN`] || ''}
                                            onChange={(e) => handleInputChange(`${memberPrefix}TFN`, e.target.value)}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                            placeholder="123456789"
                                            maxLength="9"
                                        />
                                    </div>

                                    {/* Date of Birth */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Date of Birth <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={config[`${memberPrefix}DOB`] || ''}
                                            onChange={(e) => handleInputChange(`${memberPrefix}DOB`, e.target.value)}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                        />
                                    </div>

                                    {/* Address */}
                                    <div className="md:col-span-3">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Residential Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={config[`${memberPrefix}Address`] || ''}
                                            onChange={(e) => handleInputChange(`${memberPrefix}Address`, e.target.value)}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                            placeholder="Full residential address"
                                        />
                                    </div>

                                    {/* Member Type */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Member Type
                                        </label>
                                        <select
                                            value={config[`${memberPrefix}Type`] || 'accumulation'}
                                            onChange={(e) => handleInputChange(`${memberPrefix}Type`, e.target.value)}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                        >
                                            <option value="accumulation">Accumulation Only</option>
                                            <option value="pension">Pension Only</option>
                                            <option value="both">Both (Accumulation & Pension)</option>
                                        </select>
                                    </div>

                                    {/* Account Balance */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Account Balance ($)
                                        </label>
                                        <input
                                            {...getNumericInputProps()}
                                            value={config[`${memberPrefix}Balance`] || ''}
                                            onChange={(e) => handleInputChange(`${memberPrefix}Balance`, parseFloat(e.target.value) || 0)}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                            placeholder="0"
                                        />
                                    </div>

                                    {/* TBC Used */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Transfer Balance Cap Used ($)
                                        </label>
                                        <input
                                            {...getNumericInputProps()}
                                            value={config[`${memberPrefix}TBCUsed`] || ''}
                                            onChange={(e) => handleInputChange(`${memberPrefix}TBCUsed`, parseFloat(e.target.value) || 0)}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>

                                {/* Contributions Section */}
                                <div className="mt-4 p-3 bg-gray-50 rounded border border-gray-200">
                                    <h6 className="text-sm font-medium text-gray-700 mb-3">Current FY Contributions</h6>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                Concessional Contributions ($)
                                            </label>
                                            <input
                                                {...getNumericInputProps()}
                                                value={config[`${memberPrefix}ConcessionalUsed`] || ''}
                                                onChange={(e) => handleInputChange(`${memberPrefix}ConcessionalUsed`, parseFloat(e.target.value) || 0)}
                                                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                                placeholder="0"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                Non-Concessional Contributions ($)
                                            </label>
                                            <input
                                                {...getNumericInputProps()}
                                                value={config[`${memberPrefix}NonConcessionalUsed`] || ''}
                                                onChange={(e) => handleInputChange(`${memberPrefix}NonConcessionalUsed`, parseFloat(e.target.value) || 0)}
                                                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-4 p-3 bg-blue-100 rounded-lg border border-blue-300">
                    <p className="text-sm text-blue-800">
                        <strong>💡 Note:</strong> Member details are used for ATO reporting, compliance monitoring, and proportional asset allocation when transferring to retirement calculators.
                    </p>
                </div>
            </div>

            {/* Trustee Details Section */}
            {config.trusteeType === 'individual' && (
                <div className="mt-6 p-6 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-4">Individual Trustee Details</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Dynamically render trustees based on numberOfMembers */}
                        {[1, 2, 3, 4, 5, 6].slice(0, Math.min(config.numberOfMembers, 6)).map((num) => (
                            <div key={num} className="space-y-4">
                                <h5 className="font-medium text-gray-600">Trustee {num}</h5>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Full Name
                                    </label>
                                    <input
                                        type="text"
                                        value={config[`trustee${num}Name`]}
                                        onChange={(e) => handleInputChange(`trustee${num}Name`, e.target.value)}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                        placeholder={`Trustee ${num} Name`}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        TFN
                                    </label>
                                    <input
                                        type="text"
                                        value={config[`trustee${num}TFN`]}
                                        onChange={(e) => handleInputChange(`trustee${num}TFN`, e.target.value)}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                        placeholder="123456789"
                                        maxLength="9"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Date of Birth
                                    </label>
                                    <input
                                        type="date"
                                        value={config[`trustee${num}DOB`]}
                                        onChange={(e) => handleInputChange(`trustee${num}DOB`, e.target.value)}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Corporate Trustee Details Section */}
            {config.trusteeType === 'corporate' && (
                <div className="mt-6 p-6 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-4">Corporate Trustee Details</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Company Name
                            </label>
                            <input
                                type="text"
                                value={config.corporateTrusteeName}
                                onChange={(e) => handleInputChange('corporateTrusteeName', e.target.value)}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                placeholder="Smith Family SMSF Pty Ltd"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                ACN
                            </label>
                            <input
                                type="text"
                                value={config.corporateACN}
                                onChange={(e) => handleInputChange('corporateACN', e.target.value)}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                placeholder="123 456 789"
                                maxLength="9"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Director 1 Name
                            </label>
                            <input
                                type="text"
                                value={config.corporateDirector1}
                                onChange={(e) => handleInputChange('corporateDirector1', e.target.value)}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                placeholder="John Smith"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Director 2 Name
                            </label>
                            <input
                                type="text"
                                value={config.corporateDirector2}
                                onChange={(e) => handleInputChange('corporateDirector2', e.target.value)}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={inputsLocked}
                            readOnly={inputsLocked}
                                placeholder="Jane Smith"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Save Button and Status */}
            <div className="mt-6 flex items-center justify-between border-t pt-4">
                <div className="text-sm text-gray-600">
                    {saveMessage && (
                        <span className={`${saveMessage.includes('success') ? 'text-green-600' : 'text-blue-600'}`}>
                            {saveMessage}
                        </span>
                    )}
                    {!saveMessage && (
                        <span>Configuration saved at {getSaveTime()}</span>
                    )}
                </div>
                <button
                    onClick={handleSave}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-md transition-colors"
                >
                    Save Configuration
                </button>
            </div>
        </div>
    );
};


// SMSF Fee Calculator Display
export const SMSFFeeCalculatorDisplay = ({ smsfData }) => {
    const [feeResults, setFeeResults] = useState(null);
    const [comparisonResults, setComparisonResults] = useState(null);

    useEffect(() => {
        if (smsfData && smsfData.balance) {
            // REAL fee calculations using SMSFFeeEngine
            const feeResults = SMSFFeeEngine.TotalCostCalculator.calculateTotalSMSFCosts({
                balance: smsfData.balance || 0,
                trusteeType: smsfData.trusteeType || 'individual',
                memberCount: smsfData.members?.length || 2,
                complexity: smsfData.complexity || 'standard',
                assetAllocation: smsfData.assetAllocation || {},
                assetTypes: smsfData.assetTypes || {},
                transactionVolumes: smsfData.transactionVolumes || {},
                members: smsfData.members || [],
                insuranceNeeds: smsfData.insuranceNeeds || {},
                hasLRBA: smsfData.hasLRBA || false,
                valuationFrequency: smsfData.valuationFrequency || 'annual'
            });
            
            // Calculate industry fund fees for comparison
            const industryBenchmark = FinancialConfig.INDUSTRY_FUND_BENCHMARKS?.FEES || {
                ADMIN_FEE_FIXED: 130,
                ADMIN_FEE_PERCENTAGE: 0.0015,
                INVESTMENT_FEE_PERCENTAGE: 0.008,
                TYPICAL_TOTAL_FEE: 0.01
            };
            
            const industryFees = {
                admin: Math.max(industryBenchmark.ADMIN_FEE_FIXED, (smsfData.balance || 0) * industryBenchmark.ADMIN_FEE_PERCENTAGE),
                investment: (smsfData.balance || 0) * industryBenchmark.INVESTMENT_FEE_PERCENTAGE
            };
            industryFees.total = industryFees.admin + industryFees.investment;
            
            const feeDifference = industryFees.total - feeResults.totalAnnualCosts;
            const breakEvenBalance = feeResults.totalAnnualCosts / industryBenchmark.TYPICAL_TOTAL_FEE;
            
            const comparisonResults = {
                smsfFees: feeResults.totalAnnualCosts,
                industrySuperFees: Math.round(industryFees.total),
                feeDifference: Math.round(feeDifference),
                smsfFeePercentage: feeResults.costAsPercentage,
                industrySuperFeePercentage: (industryFees.total / (smsfData.balance || 1) * 100).toFixed(3),
                recommendation: feeDifference > 0 ? 'SMSF' : 'Industry Super',
                breakEvenBalance: Math.round(breakEvenBalance)
            };
            
            setFeeResults(feeResults);
            setComparisonResults(comparisonResults);
        }
    }, [smsfData]);

    if (!feeResults || !comparisonResults) {
        return <div className="text-gray-500">Loading fee calculations...</div>;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">SMSF Fee Analysis</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* SMSF Fees Breakdown */}
                <div>
                    <h4 className="font-medium text-gray-700 mb-3">SMSF Annual Costs</h4>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Trustee Fees:</span>
                            <span className="font-medium">${feeResults.breakdown.trusteeFees.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Audit Costs:</span>
                            <span className="font-medium">${feeResults.breakdown.auditCosts.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Compliance Costs:</span>
                            <span className="font-medium">${feeResults.breakdown.complianceCosts.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Investment Fees:</span>
                            <span className="font-medium">${feeResults.breakdown.investmentFees.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Insurance Costs:</span>
                            <span className="font-medium">${feeResults.breakdown.insuranceCosts.toLocaleString()}</span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between font-semibold">
                            <span>Total Annual Costs:</span>
                            <span>${feeResults.totalAnnualCosts.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Cost as % of Balance:</span>
                            <span className="font-medium">{feeResults.costAsPercentage}%</span>
                        </div>
                    </div>
                </div>

                {/* Comparison with Industry Super */}
                <div>
                    <h4 className="font-medium text-gray-700 mb-3">vs Industry Super</h4>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">SMSF Fees:</span>
                            <span className="font-medium">${comparisonResults.smsfFees.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Industry Super Fees:</span>
                            <span className="font-medium">${comparisonResults.industrySuperFees.toLocaleString()}</span>
                        </div>
                        <hr className="my-2" />
                        <div className={`flex justify-between font-semibold ${comparisonResults.feeDifference < 0 ? 'text-green-600' : 'text-red-600'}`}>
                            <span>Difference:</span>
                            <span>${Math.abs(comparisonResults.feeDifference).toLocaleString()} 
                                {comparisonResults.feeDifference < 0 ? ' savings' : ' extra cost'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Recommendation:</span>
                            <span className={`font-medium ${comparisonResults.recommendation === 'SMSF' ? 'text-green-600' : 'text-orange-600'}`}>
                                {comparisonResults.recommendation}
                            </span>
                        </div>
                        {comparisonResults.breakEvenBalance && (
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Break-even Balance:</span>
                                <span className="font-medium">${comparisonResults.breakEvenBalance.toLocaleString()}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


// SMSF Compliance Dashboard
export const SMSFComplianceDashboard = ({ smsfData }) => {
    const [complianceResults, setComplianceResults] = useState(null);

    useEffect(() => {
        if (smsfData) {
            // REAL compliance calculations using SMSFComplianceEngine
            const solePurposeTest = SMSFComplianceEngine.SolePurposeTestManager.validateSolePurposeTest(
                smsfData.investments || {},
                smsfData.expenses || {},
                smsfData.members || []
            );
            
            const lrbaCompliance = smsfData.hasLRBA ? 
                SMSFComplianceEngine.LRBAComplianceManager.validateLRBA(smsfData.lrbaDetails || {}) :
                { compliant: true, violations: [], warnings: [] };
            
            const inHouseAssetCompliance = SMSFComplianceEngine.InHouseAssetManager.validateInHouseAssets(
                smsfData.inHouseAssets || [],
                smsfData.totalAssets || smsfData.balance || 0
            );
            
            // Calculate overall compliance score
            const allViolations = [
                ...solePurposeTest.violations,
                ...(lrbaCompliance.violations || []),
                ...(inHouseAssetCompliance.violations || [])
            ];
            
            const overallCompliant = allViolations.length === 0;
            const complianceScore = Math.round((
                solePurposeTest.complianceScore +
                (lrbaCompliance.complianceScore || 100) +
                (inHouseAssetCompliance.complianceScore || 100)
            ) / 3);
            
            const recommendations = [
                ...solePurposeTest.recommendations,
                ...(lrbaCompliance.recommendations || []),
                ...(inHouseAssetCompliance.recommendations || [])
            ];
            
            setComplianceResults({
                overallCompliant,
                complianceScore,
                violations: allViolations,
                detailedResults: {
                    solePurposeTest,
                    lrbaCompliance,
                    inHouseAssetCompliance
                },
                recommendations
            });
        }
    }, [smsfData]);

    if (!complianceResults) {
        return <div className="text-gray-500">Loading compliance check...</div>;
    }

    const getRiskLevelColor = (level) => {
        switch (level) {
            case 'LOW': return 'text-green-600 bg-green-100';
            case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
            case 'HIGH': return 'text-orange-600 bg-orange-100';
            case 'CRITICAL': return 'text-red-600 bg-red-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">SMSF Compliance Dashboard</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Overall Compliance Status */}
                <div className="text-center">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${complianceResults.overallCompliant ? 'text-green-800 bg-green-100' : 'text-red-800 bg-red-100'}`}>
                        {complianceResults.overallCompliant ? '✓ Compliant' : '✗ Non-Compliant'}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Overall Status</p>
                </div>

                {/* Compliance Score */}
                <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{complianceResults.complianceScore}%</div>
                    <p className="text-sm text-gray-600">Compliance Score</p>
                </div>

                {/* Violations Count */}
                <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{complianceResults.violations.length}</div>
                    <p className="text-sm text-gray-600">Violations</p>
                </div>
            </div>

            {/* Compliance Details */}
            <div className="space-y-4">
                {/* Sole Purpose Test */}
                {complianceResults.detailedResults.solePurposeTest && (
                    <div className="border-l-4 border-blue-400 pl-4">
                        <h4 className="font-medium text-gray-700">Sole Purpose Test</h4>
                        <div className="flex items-center mt-1">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskLevelColor(complianceResults.detailedResults.solePurposeTest.riskLevel)}`}>
                                {complianceResults.detailedResults.solePurposeTest.riskLevel} RISK
                            </span>
                            <span className="ml-2 text-sm text-gray-600">
                                Score: {complianceResults.detailedResults.solePurposeTest.complianceScore}%
                            </span>
                        </div>
                    </div>
                )}

                {/* LRBA Compliance */}
                {complianceResults.detailedResults.lrbaCompliance && (
                    <div className="border-l-4 border-green-400 pl-4">
                        <h4 className="font-medium text-gray-700">LRBA Compliance</h4>
                        <div className="flex items-center mt-1">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${complianceResults.detailedResults.lrbaCompliance.compliant ? 'text-green-800 bg-green-100' : 'text-red-800 bg-red-100'}`}>
                                {complianceResults.detailedResults.lrbaCompliance.compliant ? 'Compliant' : 'Non-Compliant'}
                            </span>
                        </div>
                    </div>
                )}

                {/* In-House Asset Compliance */}
                {complianceResults.detailedResults.inHouseAssetCompliance && (
                    <div className="border-l-4 border-yellow-400 pl-4">
                        <h4 className="font-medium text-gray-700">In-House Asset Compliance</h4>
                        <div className="flex items-center mt-1">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${complianceResults.detailedResults.inHouseAssetCompliance.compliant ? 'text-green-800 bg-green-100' : 'text-red-800 bg-red-100'}`}>
                                {complianceResults.detailedResults.inHouseAssetCompliance.compliant ? 'Compliant' : 'Non-Compliant'}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Recommendations */}
            {complianceResults.recommendations.length > 0 && (
                <div className="mt-6">
                    <h4 className="font-medium text-gray-700 mb-3">Recommendations</h4>
                    <div className="space-y-2">
                        {complianceResults.recommendations.slice(0, 5).map((rec, index) => (
                            <div key={index} className="flex items-start">
                                <span className={`px-2 py-1 rounded text-xs font-medium mr-2 ${getRiskLevelColor(rec.priority)}`}>
                                    {rec.priority}
                                </span>
                                <div>
                                    <p className="text-sm font-medium">{rec.action}</p>
                                    <p className="text-xs text-gray-600">{rec.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// SMSF Investment Strategy Display - REMOVED (unused, had mock data)
// Use SMSFInvestmentStrategyBuilder component instead for real strategy generation

